import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';

const ModalStackContext = createContext();

export const useModalStack = () => {
  const context = useContext(ModalStackContext);
  if (!context) {
    throw new Error('useModalStack must be used within a ModalStackProvider');
  }
  return context;
};

export const ModalStackProvider = ({ children }) => {
  const [modalStack, setModalStack] = useState([]);
  // S3-03.2 — SUSPENSION. A dialog that is NOT a `ui/Modal` (the event
  // Log form portals and paints itself) still has to be able to say
  // "while I am open, the ff-modal underneath me is not the top dialog".
  // Without it, Escape reached BOTH: `stopPropagation` cannot help,
  // because both listeners are on `document` and same-node listeners all
  // fire regardless — and the outer modal registered first, so it wins
  // even with stopImmediatePropagation. The outer modal's Tab trap had
  // the same problem, holding focus inside a form the operator had
  // stopped looking at.
  const [suspendedBy, setSuspendedBy] = useState([]);

  // Base z-index from CSS custom properties - use a higher starting point for dynamic modals
  const baseZ = 1200; // Start above --ff-z-modal (1095) and --ff-z-drawer (1100)

  // S3-02.3 adversarial review: pushModal / popModal / value were rebuilt
  // on EVERY provider render, and ModalPortal's effect lists pushModal and
  // popModal in its dependency array. So: push -> setModalStack -> provider
  // re-renders -> new callback identities -> the portal's effect tears down
  // and re-runs -> push again -> forever. Confirmed in the browser, and it
  // hard-hung jsdom. Memoised here; the dev logging now fires only on a
  // real push/pop instead of on every spin of that loop.
  const stackRef = useRef(modalStack);
  stackRef.current = modalStack;

  // Push modal onto stack and return its z-index
  const pushModal = useCallback((modalId) => {
    // Depth BEFORE adding, read from the ref so the callback identity does
    // not have to change when the stack does.
    const depth = stackRef.current.length;
    setModalStack(prev => {
      if (prev.includes(modalId)) return prev;   // idempotent: same array, no re-render
      const newStack = [...prev, modalId];
      if (process.env.NODE_ENV === 'development') {
        console.log('[ModalStack] Push:', modalId, 'Stack depth:', newStack.length);
      }
      return newStack;
    });

    const zIndex = baseZ + (depth * 10);
    return zIndex;
  }, []);

  // Pop modal from stack
  const popModal = useCallback((modalId) => {
    setModalStack(prev => {
      if (!prev.includes(modalId)) return prev;  // idempotent: same array, no re-render
      const newStack = prev.filter(id => id !== modalId);
      if (process.env.NODE_ENV === 'development') {
        console.log('[ModalStack] Pop:', modalId, 'Stack depth:', newStack.length);
      }
      return newStack;
    });
  }, []);

  // Get z-index for a specific modal
  const getModalZIndex = useCallback((modalId) => {
    const index = stackRef.current.indexOf(modalId);
    if (index === -1) return baseZ;
    return baseZ + (index * 10);
  }, []);

  // Suspend / resume, keyed by the suspending dialog's id so two nested
  // dialogs cannot un-suspend each other, and an unmount that races a
  // re-open is idempotent either way.
  const suspendModals = useCallback((ownerId) => {
    setSuspendedBy((prev) => (prev.includes(ownerId) ? prev : [...prev, ownerId]));
  }, []);

  const resumeModals = useCallback((ownerId) => {
    setSuspendedBy((prev) => (prev.includes(ownerId)
      ? prev.filter((id) => id !== ownerId) : prev));
  }, []);

  // Get the current top modal
  const getTopModal = useCallback(() => {
    return stackRef.current[stackRef.current.length - 1] || null;
  }, []);

  // Check if any modals are open
  const hasOpenModals = modalStack.length > 0;

  // Manage body class, inert states, and UI shield
  useEffect(() => {
    const appRoot = document.getElementById('root');
    const appContent = document.querySelector('.app-content') || appRoot;
    const header = document.querySelector('header') || document.querySelector('nav');
    const facilityDrawer = document.querySelector('.facility-drawer');
    
    if (hasOpenModals) {
      // Immediate UI shield activation
      if (appRoot) {
        appRoot.setAttribute('data-ui-shield', 'active');
      }
      
      // Add modal-open class and set inert on main content
      document.body.classList.add('ff-modal-open');
      if (appContent) {
        appContent.inert = true;
        appContent.setAttribute('aria-hidden', 'true');
      }
      
      // Set inert on header and facility drawer
      if (header) {
        header.inert = true;
        header.setAttribute('aria-hidden', 'true');
      }
      if (facilityDrawer) {
        facilityDrawer.inert = true;
        facilityDrawer.setAttribute('aria-hidden', 'true');
      }
      
      // Programmatically dismiss any open tooltips/popovers
      const tooltips = document.querySelectorAll('[data-tooltip], .tooltip, .popover');
      tooltips.forEach(el => {
        if (el.style) el.style.display = 'none';
      });
      
    } else {
      // Remove UI shield
      if (appRoot) {
        appRoot.removeAttribute('data-ui-shield');
      }
      
      // Remove modal-open class and restore main content
      document.body.classList.remove('ff-modal-open');
      if (appContent) {
        appContent.inert = false;
        appContent.removeAttribute('aria-hidden');
      }
      
      // Restore header and facility drawer
      if (header) {
        header.inert = false;
        header.removeAttribute('aria-hidden');
      }
      if (facilityDrawer) {
        facilityDrawer.inert = false;
        facilityDrawer.removeAttribute('aria-hidden');
      }
      
      // Restore tooltips
      const tooltips = document.querySelectorAll('[data-tooltip], .tooltip, .popover');
      tooltips.forEach(el => {
        if (el.style && el.style.display === 'none') el.style.display = '';
      });
    }

    return () => {
      // Cleanup on unmount
      if (appRoot) {
        appRoot.removeAttribute('data-ui-shield');
      }
      if (appContent) {
        appContent.inert = false;
        appContent.removeAttribute('aria-hidden');
      }
      if (header) {
        header.inert = false;
        header.removeAttribute('aria-hidden');
      }
      if (facilityDrawer) {
        facilityDrawer.inert = false;
        facilityDrawer.removeAttribute('aria-hidden');
      }
      document.body.classList.remove('ff-modal-open');
    };
  }, [hasOpenModals]);

  const isSuspended = suspendedBy.length > 0;

  const value = useMemo(() => ({
    modalStack,
    pushModal,
    popModal,
    getModalZIndex,
    getTopModal,
    hasOpenModals,
    suspendModals,
    resumeModals,
    isSuspended,
    // R5 B2: WHO is suspending, so a ui/Modal that claims the suspension
    // itself (`suspendBeneath`) can tell its own claim from a dialog
    // layered on top of it.
    suspendedBy,
    stackDepth: modalStack.length
  }), [modalStack, pushModal, popModal, getModalZIndex, getTopModal,
    hasOpenModals, suspendModals, resumeModals, isSuspended, suspendedBy]);

  return (
    <ModalStackContext.Provider value={value}>
      {children}
    </ModalStackContext.Provider>
  );
};

export default ModalStackContext;